import React from 'react';

import encodeQr from './qrcode';

/**
 * A QR code on screen, sharp enough to scan off the glass.
 *
 * Two things decide whether a phone can read a code from a monitor, and both
 * are about pixels rather than size:
 *
 *   Whole modules. Each square of the code is drawn on an exact number of
 *   screen pixels. Let one land on a half pixel and the browser blends it
 *   grey along one edge — across a whole code that is enough smearing to
 *   stop a camera locking on. So the module size is rounded DOWN to a whole
 *   number and the canvas is sized to match, rather than the code being
 *   stretched to fill a box someone picked.
 *
 *   The quiet zone. Four empty modules all the way round, which the spec
 *   requires and scanners genuinely need — it is how the camera works out
 *   where the code ends. It is drawn as part of the canvas so no surrounding
 *   layout can eat it.
 *
 * The canvas is also drawn at the screen's own pixel density, so it stays
 * crisp on the high-resolution laptop screens some of these tills are.
 */

const QUIET = 4;

export default function QrCanvas({
  value,
  targetSize = 240,
  dark = '#15181D',
  light = '#FFFFFF',
}) {
  const canvasRef = React.useRef(null);
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !value) return;

    let code;
    try {
      code = encodeQr(value);
      setError('');
    } catch (err) {
      setError(err?.message || 'Could not build the code');
      return;
    }

    const total = code.size + QUIET * 2;

    // Rounded down, never below 1 — a code drawn at less than one pixel per
    // module is not a code.
    const modulePx = Math.max(1, Math.floor(targetSize / total));
    const cssSize = modulePx * total;

    const ratio = Math.max(1, Math.min(3, window.devicePixelRatio || 1));
    canvas.width = cssSize * ratio;
    canvas.height = cssSize * ratio;
    canvas.style.width = `${cssSize}px`;
    canvas.style.height = `${cssSize}px`;

    const ctx = canvas.getContext('2d');
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0);

    ctx.fillStyle = light;
    ctx.fillRect(0, 0, cssSize, cssSize);

    ctx.fillStyle = dark;
    for (let r = 0; r < code.size; r += 1) {
      for (let c = 0; c < code.size; c += 1) {
        if (!code.modules[r][c]) continue;
        ctx.fillRect((c + QUIET) * modulePx, (r + QUIET) * modulePx, modulePx, modulePx);
      }
    }

    // Nothing is handed back up. What gets downloaded is encoded again at a
    // far higher resolution in downloadQrPng.js — the encoder is
    // deterministic, so it is the same code, just with enough pixels to
    // survive being printed.
  }, [value, targetSize, dark, light]);

  if (error) {
    return (
      <div
        style={{
          width: targetSize,
          height: targetSize,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 16,
          border: '1px solid #E6E8EC',
          borderRadius: 10,
          fontSize: 12,
          color: '#DC2626',
          textAlign: 'center',
          boxSizing: 'border-box',
        }}
      >
        {error}
      </div>
    );
  }

  return (
    <canvas
      ref={canvasRef}
      role="img"
      aria-label={`QR code for ${value}`}
      style={{ display: 'block', borderRadius: 6 }}
    />
  );
}
